import { clinicalHtmlToPlainPreview, clinicalBodyIsEmpty } from './clinicalHtml';
import { normalizePrescriptionType, prescriptionTypeLabel } from './prescriptionTypes';

const formatDatePt = (value?: string | null) => {
  if (!value) return '';
  const raw = String(value).split('T')[0];
  const dt = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return raw;
  return dt.toLocaleDateString('pt-BR');
};

export const isLocalPrescriptionModel = (prescription: any) =>
  String(prescription?.origin || 'local').toLowerCase() !== 'memed';

const filledPrescriptionItem = (item: any) =>
  String(item?.medication_name || '').trim() !== '';

const stripImportedItemIds = (item: any, emptyItem: Record<string, unknown>, sortOrder: number) => ({
  ...emptyItem,
  ...item,
  id: undefined,
  prescription_id: undefined,
  template_id: undefined,
  created_at: undefined,
  updated_at: undefined,
  sort_order: sortOrder,
});

const mergeNotes = (current?: string, incoming?: string) => {
  const a = String(current || '').trim();
  const b = String(incoming || '').trim();
  if (!a) return b;
  if (!b) return a;
  if (a.includes(b)) return a;
  return `${a}\n\n${b}`;
};

/** Rótulo legível para um receituário anterior do paciente. */
export function prescriptionHistoryLabel(prescription: any): string {
  const date = formatDatePt(prescription?.issue_date || prescription?.created_at);
  const type = prescriptionTypeLabel(String(prescription?.prescription_type || 'simple'));
  const meds = (prescription?.items || [])
    .map((item: any) => String(item?.medication_name || '').trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');
  const freeText =
    prescription?.format === 'free_text'
      ? clinicalHtmlToPlainPreview(prescription?.body_text || '', 48)
      : '';
  const content = meds || freeText || 'Receituário';
  const number = prescription?.prescription_number ? `#${prescription.prescription_number}` : '';
  return [date, type, content, number].filter(Boolean).join(' · ');
}

export function prescriptionHistorySubtitle(prescription: any): string {
  const professional = prescription?.professional_name || prescription?.professional?.full_name;
  const parts = [professional].filter(Boolean);
  const medCount = Number(prescription?.item_count);
  if (Number.isFinite(medCount) && medCount > 0) {
    parts.push(`${medCount} medicamento${medCount > 1 ? 's' : ''}`);
  }
  if (prescription?.status === 'cancelled') parts.push('Cancelado');
  return parts.join(' · ');
}

/** Linhas para tooltip / detalhe do histórico (todos os medicamentos ou texto livre). */
export function prescriptionHistoryMedicationLines(prescription: any): string[] {
  if (prescription?.format === 'free_text') {
    const text = clinicalHtmlToPlainPreview(prescription?.body_text || '', 800).trim();
    return text ? [text] : [];
  }
  const items = Array.isArray(prescription?.items) ? prescription.items : [];
  const lines = items
    .map((item: any) => {
      const name = String(item?.medication_name || '').trim();
      if (!name) return '';
      const details = [item.dosage, item.frequency, item.quantity, item.concentration]
        .map((v) => String(v || '').trim())
        .filter(Boolean)
        .join(' · ');
      return details ? `${name} — ${details}` : name;
    })
    .filter(Boolean);
  return lines;
}

export function filterPatientPrescriptionHistory(list: any[], query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((prescription) => {
    const haystack = [
      prescriptionHistoryLabel(prescription),
      prescriptionHistorySubtitle(prescription),
      prescription?.prescription_number,
      ...(prescription?.items || []).map((item: any) => item?.medication_name),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

type SetStateAction<T> = T | ((prev: T) => T);

export interface ApplyPrescriptionModelHandlers {
  setPrescriptionForm: (value: SetStateAction<any>) => void;
  setFormat: (value: SetStateAction<'structured' | 'free_text'>) => void;
  setBodyText: (value: SetStateAction<string>) => void;
  setItems: (value: SetStateAction<any[]>) => void;
}

/** Adiciona itens/campos de um modelo ou receituário anterior ao formulário atual (não substitui). */
export function applyPrescriptionModelToForm(
  source: any,
  handlers: ApplyPrescriptionModelHandlers,
  emptyItem: Record<string, unknown>,
  context?: {
    items?: any[];
    format?: 'structured' | 'free_text';
    bodyText?: string;
  },
) {
  const sourceFormat = source?.format === 'free_text' ? 'free_text' : 'structured';
  const sourceBody = sourceFormat === 'free_text' ? String(source?.body_text || '') : '';
  const importedItems = (source?.items || [])
    .map((item: any, idx: number) => stripImportedItemIds(item, emptyItem, idx))
    .filter(filledPrescriptionItem);

  const existingFilled = (context?.items || []).filter(filledPrescriptionItem);
  const hasStructuredContent = existingFilled.length > 0 || importedItems.length > 0;

  handlers.setPrescriptionForm((prev: any) => ({
    ...prev,
    prescription_type: normalizePrescriptionType(source?.prescription_type) || prev.prescription_type,
    diagnosis: prev.diagnosis || source?.diagnosis || source?.default_diagnosis || '',
    diagnosis_code: prev.diagnosis_code || source?.diagnosis_code || source?.default_diagnosis_code || '',
    clinical_indication: prev.clinical_indication || source?.clinical_indication || '',
    notes: mergeNotes(
      prev.notes,
      (!importedItems.length && sourceFormat === 'free_text' && hasStructuredContent && !clinicalBodyIsEmpty(sourceBody))
        ? clinicalHtmlToPlainPreview(sourceBody, 2000)
        : (source?.notes || source?.default_notes),
    ),
    renewal_date: prev.renewal_date || '',
  }));

  handlers.setItems((prev) => {
    const currentFilled = (prev || []).filter(filledPrescriptionItem);
    const merged = [...currentFilled, ...importedItems];
    if (merged.length === 0) return [{ ...emptyItem, sort_order: 0 }];
    return merged.map((item, idx) => ({ ...item, sort_order: idx }));
  });

  if (importedItems.length > 0 || hasStructuredContent) {
    handlers.setFormat('structured');
    return;
  }

  if (sourceFormat === 'free_text' && !clinicalBodyIsEmpty(sourceBody)) {
    handlers.setFormat('free_text');
    handlers.setBodyText((prev) => {
      const base = prev || context?.bodyText || '';
      if (clinicalBodyIsEmpty(base)) return sourceBody;
      return `${base}<hr/>${sourceBody}`;
    });
  }
}
